import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";
import { MezzoFields } from "@/components/ChecklistMezzo";

function ItemRow({ idx, item, value, onChange }) {
  const set = (patch) => onChange(idx, { ...value, ...patch });
  return (
    <div data-testid={`checklist-row-${idx}`} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_3rem_7rem_1fr] gap-2 items-center py-2 border-b border-border last:border-0">
      <span className="text-sm">{item.name}</span>
      <span className="text-xs font-mono text-muted-foreground text-right sm:text-center">{item.qty || "-"}</span>
      <div className="flex gap-1">
        <Button type="button" size="sm" data-testid={`checklist-si-${idx}`} variant={value.presente === true ? "default" : "outline"} onClick={() => set({ presente: true })} className={`h-8 flex-1 rounded-full ${value.presente === true ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}>SI</Button>
        <Button type="button" size="sm" data-testid={`checklist-no-${idx}`} variant={value.presente === false ? "destructive" : "outline"} onClick={() => set({ presente: false })} className="h-8 flex-1 rounded-full">NO</Button>
      </div>
      <Input data-testid={`checklist-note-${idx}`} value={value.note} onChange={(e) => set({ note: e.target.value })} placeholder="Note / eccezioni" className="h-8 text-sm col-span-2 sm:col-span-1" />
      {item.has_expiry && (
        <div className="col-span-2 sm:col-span-4 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium">Scadenza più vicina</span>
          <Input type="date" data-testid={`checklist-expiry-${idx}`} value={value.expiry || ""} onChange={(e) => set({ expiry: e.target.value, expiry_confirmed: !!e.target.value })} className="h-8 text-sm w-44" />
          {value.expiry && (value.expiry_confirmed ? (
            <span className="text-emerald-700 font-semibold" data-testid={`checklist-expiry-ok-${idx}`}>Verificata</span>
          ) : (
            <Button type="button" size="sm" variant="outline" data-testid={`checklist-expiry-confirm-${idx}`} onClick={() => set({ expiry_confirmed: true })} className="h-7 rounded-full text-xs border-orange-300 text-orange-700">Conferma data</Button>
          ))}
          {value.presente === true && !(value.expiry && value.expiry_confirmed) && <span className="text-orange-700">obbligatoria</span>}
        </div>
      )}
    </div>
  );
}

export function CrewTable({ crew }) {
  if (!crew.length) return null;
  return (
    <div className="rounded-lg border border-border overflow-x-auto" data-testid="checklist-crew">
      <p className="bg-muted px-3 py-2 text-xs font-bold uppercase tracking-wide">Volontari assegnati al servizio</p>
      <table className="w-full text-sm">
        <thead><tr className="text-xs text-muted-foreground text-left"><th className="px-3 py-1.5">Nominativo</th><th className="px-3">Ruolo</th><th className="px-3">Mezzo / Tipo</th><th className="px-3">Targa</th></tr></thead>
        <tbody>
          {crew.map((m, i) => (
            <tr key={i} data-testid={`crew-row-${i}`} className="border-t border-border">
              <td className="px-3 py-1.5 font-medium">{m.name}</td>
              <td className="px-3">{m.role}</td>
              <td className="px-3">{[m.vehicle, m.vehicle_type].filter(Boolean).join(" - ") || "-"}</td>
              <td className="px-3 font-mono">{m.plate || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const FOOTNOTES = {
  vano: "*Verificare il funzionamento pre-operativo",
  zaino: "*Rispettare le indicazioni relative ai farmaci a temperatura controllata, se presenti.",
  mezzo: "N.B. Verifica dei gruppi: es. gruppo ottico, controllare tutte le luci di posizione, anabbaglianti, abbaglianti, indicatori di posizione, luci di arresto, ecc.",
};

function RoleChoice({ value, onChange }) {
  return (
    <div className="flex gap-2" data-testid="checklist-role">
      {["medico", "infermiere"].map((r) => (
        <Button key={r} type="button" size="sm" variant={value === r ? "default" : "outline"} data-testid={`checklist-role-${r}`}
          onClick={() => onChange(value === r ? "" : r)} className="rounded-full h-8 capitalize">{r}</Button>
      ))}
    </div>
  );
}

function TemperatureCheck({ temp, setTemp, ok, setOk }) {
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid="checklist-temperature">
      <p className="text-xs font-bold uppercase tracking-wide">Farmaci a temperatura controllata</p>
      <div className="flex flex-wrap items-center gap-3">
        <Label className="text-sm">Temperatura rilevata (°C)</Label>
        <Input type="number" step="0.1" value={temp} onChange={(e) => setTemp(e.target.value)} data-testid="checklist-temp-input" className="h-8 w-24 text-sm" />
        <Label className="text-sm">Conservazione corretta</Label>
        <div className="flex gap-1">
          <Button type="button" size="sm" data-testid="checklist-conserv-si" variant={ok === true ? "default" : "outline"} onClick={() => setOk(true)} className={`h-8 rounded-full ${ok === true ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}>SI</Button>
          <Button type="button" size="sm" data-testid="checklist-conserv-no" variant={ok === false ? "destructive" : "outline"} onClick={() => setOk(false)} className="h-8 rounded-full">NO</Button>
        </div>
      </div>
      {ok === false && <p className="text-xs text-red-700">Alla firma verranno avvisati i coordinatori.</p>}
    </div>
  );
}

export function ChecklistForm({ svc, currentUser, onSaved, onCancel, kind = "vano" }) {
  const [role, setRole] = useState("");
  const [temp, setTemp] = useState("");
  const [conservOk, setConservOk] = useState(null);
  const [km, setKm] = useState("");
  const [livelli, setLivelli] = useState({});
  const [danni, setDanni] = useState([]);
  const [annotazioni, setAnnotazioni] = useState("");
  const [template, setTemplate] = useState([]);
  const [values, setValues] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState(svc.ambulances?.[0]?.id || "");
  const [extra, setExtra] = useState("");
  const [firma, setFirma] = useState("");
  const [signedAt, setSignedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/checklist/template", { params: { kind } }).then((r) => { setTemplate(r.data); setValues(r.data.map((t) => ({ item_id: t.id, presente: null, note: "" }))); });
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, [kind]);

  const [crew, setCrew] = useState([]);
  useEffect(() => {
    api.get(`/services/${svc.id}/checklist-crew`, { params: vehicleId ? { vehicle_id: vehicleId } : {} }).then((r) => setCrew(r.data)).catch(() => {});
  }, [svc.id, vehicleId]);

  useEffect(() => {
    if (!vehicleId || !template.length) return;
    api.get("/expiries", { params: { vehicle_id: vehicleId } }).then((r) => {
      const map = Object.fromEntries(r.data.map((e) => [e.item_id, e.expiry]));
      setValues((p) => p.map((v) => (map[v.item_id] && !v.expiry_confirmed ? { ...v, expiry: map[v.item_id], expiry_confirmed: false } : v)));
    }).catch(() => {});
  }, [vehicleId, template.length]);

  const sections = useMemo(() => {
    const out = [];
    template.forEach((it, i) => {
      if (!out.length || out[out.length - 1].title !== it.section) out.push({ title: it.section, rows: [] });
      out[out.length - 1].rows.push({ it, i });
    });
    return out;
  }, [template]);

  const answered = values.filter((v) => v.presente !== null).length;
  const onRow = (i, v) => setValues((p) => p.map((x, j) => (j === i ? v : x)));
  const markAllSi = () => setValues((p) => p.map((x) => (x.presente === null ? { ...x, presente: true } : x)));
  const onSign = (v) => { setFirma(v); setSignedAt(v ? new Date() : null); };

  const save = async () => {
    if (answered < values.length) return toast.error(`Rispondi SI o NO a tutte le voci (${answered}/${values.length})`);
    const unconfirmed = template.filter((t, i) => t.has_expiry && values[i].presente === true && !(values[i].expiry && values[i].expiry_confirmed));
    if (unconfirmed.length) return toast.error(`Inserisci e conferma la scadenza di: ${unconfirmed.slice(0, 3).map((t) => t.name).join(", ")}${unconfirmed.length > 3 ? ` e altre ${unconfirmed.length - 3}` : ""}`);
    if (kind === "zaino" && (temp === "" || conservOk === null)) return toast.error("Indica la temperatura rilevata e se la conservazione dei farmaci è corretta");
    if (kind === "mezzo" && !vehicleId) return toast.error("Seleziona l'ambulanza");
    if (kind === "mezzo" && km === "") return toast.error("Inserisci i Km del mezzo");
    if (kind === "mezzo" && Object.keys(livelli).length < 4) return toast.error("Indica tutti i livelli (carburante, olio, acqua, tergivetro)");
    if (!firma) return toast.error("La firma è obbligatoria");
    setSaving(true);
    try {
      await api.post(`/services/${svc.id}/checklists`, { kind, sanitario_role: role || null, temperatura: kind === "zaino" ? Number(temp) : null, conservazione_ok: kind === "zaino" ? conservOk : null, ...(kind === "mezzo" ? { km: Number(km), livelli, danni, annotazioni } : {}), vehicle_id: vehicleId || null, extra_operators: extra, items: values, firma });
      toast.success("Check-list salvata");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="checklist-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>{kind === "mezzo" ? "Ambulanza (obbligatoria)" : "Ambulanza (facoltativa)"}</Label>
          <Select value={vehicleId || "none"} onValueChange={(v) => setVehicleId(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="checklist-vehicle-select"><SelectValue placeholder="Seleziona mezzo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nessuna</SelectItem>
              {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.plate ? ` (${v.plate})` : ""}{(svc.ambulances || []).some((a) => a.id === v.id) ? " · del servizio" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          {svc.ambulances?.length > 0 && <p className="text-[11px] text-muted-foreground" data-testid="checklist-vehicle-hint">Proposta l'ambulanza assegnata al servizio: puoi cambiarla.</p>}
        </div>
        <div className="space-y-1">
          <Label>{kind === "zaino" ? "Sanitario che compila" : kind === "mezzo" ? "Autista" : "Operatori che compilano"}</Label>
          <p className="text-sm font-medium" data-testid="checklist-compiler">{currentUser.name}</p>
          {kind === "zaino" && <RoleChoice value={role} onChange={setRole} />}
          <Input data-testid="checklist-extra-operators" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Altri operatori (nome e cognome)" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground" data-testid="checklist-progress">Voci compilate: {answered}/{values.length}</span>
        <Button type="button" size="sm" variant="outline" onClick={markAllSi} data-testid="checklist-all-si" className="rounded-full">Segna restanti come SI</Button>
      </div>
      {sections.map((s) => (
        <div key={s.title} className="rounded-lg border border-border">
          <div className="hidden sm:grid grid-cols-[1fr_3rem_7rem_1fr] gap-2 bg-muted px-3 py-2 text-xs font-bold uppercase tracking-wide">
            <span>{s.title}</span><span className="text-center">n°</span><span className="text-center">Presente</span><span>Note</span>
          </div>
          <p className="sm:hidden bg-muted px-3 py-2 text-xs font-bold uppercase tracking-wide">{s.title}</p>
          <div className="px-3">
            {s.rows.map(({ it, i }) => <ItemRow key={i} idx={i} item={it} value={values[i]} onChange={onRow} />)}
          </div>
        </div>
      ))}
      <p className="text-xs italic text-muted-foreground">{FOOTNOTES[kind]}</p>
      {kind === "zaino" && <TemperatureCheck temp={temp} setTemp={setTemp} ok={conservOk} setOk={setConservOk} />}
      {kind === "mezzo" && <MezzoFields km={km} setKm={setKm} livelli={livelli} setLivelli={setLivelli} danni={danni} setDanni={setDanni} annotazioni={annotazioni} setAnnotazioni={setAnnotazioni} />}
      <CrewTable crew={crew} />
      <div className="max-w-sm">
        <SignaturePad label={kind === "mezzo" ? "Firma Autista" : "Firma dell'operatore"} testid="checklist-signature" value={firma} onChange={onSign} />
        {signedAt && <p className="text-xs text-emerald-700 font-medium" data-testid="checklist-signed-at">Firmato il {signedAt.toLocaleString("it-IT")}</p>}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} data-testid="checklist-cancel">Annulla</Button>
        <Button onClick={save} disabled={saving} data-testid="checklist-save" className="rounded-full">{saving ? "Salvataggio..." : "Salva check-list"}</Button>
      </div>
    </div>
  );
}
